/* eslint-disable react/no-unused-prop-types */
/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable max-len */
/* istanbul ignore file */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

export const ResizedVideoView = (props) => {
  const {
    maxWidth,
    height: imageOriginHeight,
    width: imageOriginWidth,
    ratio,
    src,
    thumbnailUrl,
    hideControls,
    isCurrent,
    activityLogger,
    onPlayStatusUpdate,
  } = props;
  const maxHeight = maxWidth / ratio;
  const ref = React.useRef();

  React.useEffect(() => {
    if (ref.current && src) {
      // mark start point on first play
      let totalBufferStartPoint = 0;
      let played = false;
      /* istanbul ignore next */
      const onPlay = () => {
        onPlayStatusUpdate(true);
        // only fires on first play event
        if (played) {
          return;
        }
        played = true;
        totalBufferStartPoint = Date.now();
      };
      const firstBufferStartPoint = Date.now();
      /* istanbul ignore next */
      const onCanPlay = () => {
        if (!ref.current) return;
        activityLogger.signal({
          duration: ref.current.duration,
          imageOriginWidth,
          imageOriginHeight,
          playerWidth: maxWidth,
          playerHeight: maxHeight,
          timeCost: Date.now() - firstBufferStartPoint,
        }, 'video preview first buffer');
      };
      /* istanbul ignore next */
      const onProgress = () => {
        if (!ref.current) return;
        if (played) {
          const timeRange = ref.current.buffered;
          if (timeRange.length === 1) {
            if (timeRange.start(0) === 0 && timeRange.end(0) === ref.current.duration) {
              activityLogger.signal({
                duration: ref.current.duration,
                imageOriginWidth,
                imageOriginHeight,
                playerWidth: maxWidth,
                playerHeight: maxHeight,
                timeCost: Date.now() - totalBufferStartPoint,
              }, 'video preview total buffer');
            }
          }
        }
      };
      /* istanbul ignore next */
      const onError = (event) => {
        if (!ref.current) return;
        activityLogger.error(JSON.stringify({
          duration: ref.current.duration,
          imageOriginWidth,
          imageOriginHeight,
          playerWidth: maxWidth,
          playerHeight: maxHeight,
          src,
          event,
        }), 'video preview error');
      };
      const onPause = () => {
        onPlayStatusUpdate(false);
      };
      ref.current.addEventListener('canplay', onCanPlay, { once: true });
      ref.current.addEventListener('progress', onProgress);
      ref.current.addEventListener('play', onPlay);
      ref.current.addEventListener('pause', onPause);
      ref.current.addEventListener('error', onError);
      const { current } = ref;
      return () => {
        current.removeEventListener('progress', onProgress);
        current.removeEventListener('play', onPlay);
        current.removeEventListener('pause', onPause);
        current.removeEventListener('error', onError);
      };
    }
    return _.noop;
  }, [activityLogger, imageOriginHeight, imageOriginWidth, maxHeight, maxWidth, onPlayStatusUpdate, src]);

  React.useEffect(() => {
    if (!isCurrent && ref.current) {
      ref.current.pause();
    }
    // fix control panel style issue caused by bootstrap carousel slide animation
    if (isCurrent && ref.current) {
      setTimeout(() => ref.current.focus(), 600);
    }
  }, [isCurrent]);

  return (
    <div
      className="resized-video-view"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: maxWidth,
        height: maxHeight,
      }}
    >
      <video
        width={`${maxWidth}px`}
        height={`${maxHeight}px`}
        poster={thumbnailUrl}
        controls={!hideControls}
        src={src}
        ref={ref}
        controlsList="nodownload nofullscreen"
        disablePictureInPicture
      />
    </div>);
};

ResizedVideoView.propTypes = {
  height: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  ratio: PropTypes.number.isRequired,
  maxWidth: PropTypes.number.isRequired,
  src: PropTypes.string,
  thumbnailUrl: PropTypes.string,
  hideControls: PropTypes.bool,
  isCurrent: PropTypes.bool,
  activityLogger: PropTypes.shape({
    signal: PropTypes.func.isRequired,
    error: PropTypes.func.isRequired,
  }),
  onPlayStatusUpdate: PropTypes.func,
};

ResizedVideoView.defaultProps = {
  src: null,
  thumbnailUrl: null,
  hideControls: false,
  isCurrent: false,
  activityLogger: {
    signal: _.noop,
    error: _.noop,
  },
  onPlayStatusUpdate: _.noop,
};
